/**
 * @file Everything a derived value can be authored against on the CURRENT draft
 * layout: the form's fields, its entity-reference slots (with each referenced
 * record's own fields and reference keys), and its ArTables with their columns.
 * Consumer is `FormDeriveEntryPanel` — the drawer's every select reads this one
 * pass, so no picker can offer something another refuses. Pure: the caller owns
 * the subscriptions that produce `structure`, `fieldTypes`, `entities`,
 * `tableTypes` and `compositeFieldTypes`. Invariants: both engine dot tests are
 * applied here and nowhere else in `react/`, because a picker offering what the
 * config validator drops authors entries that vanish on save with nothing to
 * explain it — a column is listed when `isColumnPathTypeId` admits it, and
 * `readable` is `isColumnTypeId`, the narrower test the four FLAT source
 * positions take. A composite's `subFields` are the dotted addresses legal in
 * the subsystem's two dotted positions, a projection's declared column and a
 * column arm's `sourceCell`; the `sourceCell` select composes `readable` with
 * `subFields` rather than reading a universe field of its own, because both
 * positions offer the same addresses off the same column. `valueParts` stay the
 * differently spelled, aggregatable `aggregationFieldPath` positions.
 * `formulaOwned` comes from `resolveArFormulaOwnedTypeIds`, which owns a dotted
 * target's root as well, so a sub-field is formula-owned exactly when its column
 * is.
 */
import {
  UIElementType,
  isColumnPathTypeId,
  isColumnTypeId,
  resolveArFormulaOwnedTypeIds,
} from '#types';
import type {
  ArTableType,
  CompositeFieldType,
  Entity,
  FieldType,
  UIElement,
} from '#types';
import { layoutUniverses } from '@/services/editorExtractors';
import { formDeriveColumnSubFields } from './formDeriveColumnSubFields';
import type { FormDeriveColumnSubField } from './formDeriveColumnSubFields';
import { formDeriveColumnValueParts } from './formDeriveColumnValueParts';
import type { FormDeriveColumnValuePart } from './formDeriveColumnValueParts';

/** One header field of the form: a `FormDeriveFieldTarget` or a `field` source. */
export type FormDeriveUniverseField = {
  fieldId: string;
  label: string;
  required: boolean;
};

/** A property of a referenced record, addressable through `FormDeriveReferenceProp`. */
export type FormDeriveUniverseReference = {
  /** `entityGroupId ?? entityId`, the key `FormVersion.entityReferences` uses. */
  slotKey: string;
  label: string;
  /** Fields of the referenced record's own layout, for the `field` property kind. */
  fields: FormDeriveUniverseField[];
  /** Reference keys on the referenced record, for the `reference` property kind. */
  references: { referenceKey: string; label: string }[];
};

export type FormDeriveUniverseColumn = {
  typeId: string;
  label: string;
  /**
   * A SOURCE position may offer this column — the partition key, a `sourceCell`,
   * a `groupAggregate` or a `rowReferenceProp`. False for a dotted typeId, which
   * the three FLAT arms would look for at the key `cells['parent.sub']`. A
   * `sourceCell` reaches a sub-field by offering this column's `subFields`
   * alongside it, so no wider flag is needed.
   */
  readable: boolean;
  /** A table formula already writes this column, so a derive may not target it. */
  formulaOwned: boolean;
  /**
   * The composite sub-fields a row projection may declare as target columns, and
   * — off a `readable` column — read back through a `sourceCell`.
   */
  subFields: FormDeriveColumnSubField[];
  /** The dotted `aggregationFieldPath` positions inside a composite column. */
  valueParts: FormDeriveColumnValuePart[];
};

export type FormDeriveUniverseTable = {
  tableId: string;
  label: string;
  /** Whole columns only; a table absent from `tableTypes` contributes none. */
  columns: FormDeriveUniverseColumn[];
};

export type FormDeriveTargetUniverseInput = {
  structure: UIElement | undefined;
  fieldTypes: Record<string, FieldType>;
  entities: Record<string, Entity>;
  tableTypes: readonly ArTableType[];
  /** The composite registry, read from the app's existing cache by the caller. */
  compositeFieldTypes: readonly CompositeFieldType[];
};

export type FormDeriveTargetUniverseValue = {
  fields: FormDeriveUniverseField[];
  references: FormDeriveUniverseReference[];
  tables: FormDeriveUniverseTable[];
};

function columnLabel(column: { descriptionOverride?: { shortLabel?: string }; cacheDescription?: string; typeId: string }): string {
  return column.descriptionOverride?.shortLabel ?? column.cacheDescription ?? column.typeId;
}

function formDeriveTargetUniverse(
  input: FormDeriveTargetUniverseInput,
): FormDeriveTargetUniverseValue {
  const { structure, fieldTypes, entities, tableTypes, compositeFieldTypes } = input;
  const universes = layoutUniverses(structure, fieldTypes, entities);

  const fields = universes.fields.map((field) => ({
    fieldId: field.id,
    label: field.label,
    required: field.required === true,
  }));

  const references = universes.references.map((reference) => {
    const record = layoutUniverses(
      entities[reference.entityId]?.defaultUILayout?.structure,
      fieldTypes,
      entities,
    );
    return {
      slotKey: reference.slotKey,
      label: reference.label,
      fields: record.fields.map((field) => ({
        fieldId: field.id,
        label: field.label,
        required: field.required === true,
      })),
      references: record.references.map((nested) => ({
        referenceKey: nested.slotKey,
        label: nested.label,
      })),
    };
  });

  const tables = universes.tables.map((table) => {
    const tableType = tableTypes.find((candidate) => candidate.docId === table.tableId);
    const owned = resolveArFormulaOwnedTypeIds(tableType?.formulaSequence ?? []);
    const columns = (tableType?.columns ?? []).flatMap((column) =>
      isColumnPathTypeId(column.typeId)
        ? [
            {
              typeId: column.typeId,
              label: columnLabel(column),
              readable: isColumnTypeId(column.typeId),
              formulaOwned: owned.has(column.typeId),
              subFields:
                column.uiElementType === UIElementType.Field
                  ? formDeriveColumnSubFields(column.typeId, fieldTypes, compositeFieldTypes)
                  : [],
              valueParts:
                column.uiElementType === UIElementType.Field
                  ? formDeriveColumnValueParts(column.typeId, fieldTypes, compositeFieldTypes)
                  : [],
            },
          ]
        : [],
    );
    return {
      tableId: table.tableId,
      label: tableType?.description?.shortLabel ?? table.label,
      columns,
    };
  });

  return { fields, references, tables };
}

export { formDeriveTargetUniverse };
